import React, { useState, useEffect, useCallback } from "react";
import { fetchApi } from "../api/client";
import { CheckCircle2, XCircle, Clock, Image } from "lucide-react";

const STATUS_COLORS = {
    pending: { bg: "#fffbeb", border: "#f59e0b", text: "#92400e" },
    approved: { bg: "#f0fdf4", border: "#22c55e", text: "#166534" },
    rejected: { bg: "#fef2f2", border: "#ef4444", text: "#991b1b" },
};

function AssetReview() {
    const [requests, setRequests] = useState([]);
    const [loading, setLoading] = useState(true);
    const [statusFilter, setStatusFilter] = useState("pending");
    const [actionMsg, setActionMsg] = useState(null);
    const [actioningId, setActioningId] = useState(null);
    const [rejectNotes, setRejectNotes] = useState("");
    const [showRejectModal, setShowRejectModal] = useState(null);
    const [previewImage, setPreviewImage] = useState(null);

    const showMsg = (msg, type = "ok") => {
        setActionMsg({ text: msg, type });
        setTimeout(() => setActionMsg(null), 3000);
    };

    const loadRequests = useCallback(async () => {
        try {
            const params = statusFilter ? `?status=${statusFilter}` : "";
            const data = await fetchApi(`/assets/barcode-requests${params}`);
            setRequests(data.requests || []);
        } catch (err) {
            showMsg(err.message, "err");
        } finally {
            setLoading(false);
        }
    }, [statusFilter]);

    useEffect(() => { setLoading(true); loadRequests(); }, [loadRequests]);

    const handleApprove = async (id) => {
        setActioningId(id);
        try {
            await fetchApi(`/assets/barcode-requests/${id}/approve`, {
                method: "POST",
                body: JSON.stringify({ notes: "" }),
            });
            showMsg("Request approved");
            loadRequests();
        } catch (err) {
            showMsg(err.message, "err");
        } finally {
            setActioningId(null);
        }
    };

    const handleReject = async () => {
        if (!showRejectModal) return;
        setActioningId(showRejectModal);
        try {
            await fetchApi(`/assets/barcode-requests/${showRejectModal}/reject`, {
                method: "POST",
                body: JSON.stringify({ notes: rejectNotes }),
            });
            showMsg("Request rejected");
            setShowRejectModal(null);
            setRejectNotes("");
            loadRequests();
        } catch (err) {
            showMsg(err.message, "err");
        } finally {
            setActioningId(null);
        }
    };

    const counts = {
        pending: requests.length, // current filter count
    };

    return (
        <div style={{ padding: "16px 24px", maxWidth: 1000, margin: "0 auto" }}>
            <div style={{ marginBottom: 20 }}>
                <h2 style={{ margin: "0 0 4px" }}>Barcode Review Queue</h2>
                <p style={{ color: "#888", margin: 0, fontSize: 14 }}>
                    Review and approve new barcode sticker requests submitted by site staff.
                </p>
            </div>

            {actionMsg && (
                <div style={{
                    padding: "8px 14px", borderRadius: 6, marginBottom: 12, fontSize: 14,
                    background: actionMsg.type === "err" ? "#fef2f2" : "#f0fdf4",
                    color: actionMsg.type === "err" ? "#dc2626" : "#16a34a",
                    border: `1px solid ${actionMsg.type === "err" ? "#fecaca" : "#bbf7d0"}`,
                }}>
                    {actionMsg.text}
                </div>
            )}

            {/* Status filter pills */}
            <div style={{ display: "flex", gap: 6, marginBottom: 16 }}>
                {["pending", "approved", "rejected", ""].map(s => (
                    <button
                        key={s}
                        onClick={() => setStatusFilter(s)}
                        style={{
                            padding: "6px 14px", borderRadius: 6, fontSize: 13, cursor: "pointer",
                            border: statusFilter === s ? `1px solid ${STATUS_COLORS[s || "pending"]?.border || "#2563eb"}` : "1px solid #e2e8f0",
                            background: statusFilter === s ? (STATUS_COLORS[s || "pending"]?.bg || "#eff6ff") : "#fff",
                            color: statusFilter === s ? (STATUS_COLORS[s || "pending"]?.text || "#2563eb") : "#64748b",
                            fontWeight: statusFilter === s ? 600 : 400,
                        }}
                    >
                        {s === "" ? "All" : s.charAt(0).toUpperCase() + s.slice(1)}
                    </button>
                ))}
            </div>

            {loading ? (
                <div style={{ textAlign: "center", padding: 40, color: "#888" }}>Loading...</div>
            ) : requests.length === 0 ? (
                <div style={{ textAlign: "center", padding: 40, color: "#888" }}>
                    {statusFilter === "pending" ? "No pending requests." : "No requests found."}
                </div>
            ) : (
                <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
                    {requests.map(r => {
                        const sc = STATUS_COLORS[r.status] || STATUS_COLORS.pending;
                        let photos = [];
                        try { photos = JSON.parse(r.photo_paths || "[]"); } catch {}

                        return (
                            <div key={r.id} style={{
                                border: `1px solid ${sc.border}`, borderRadius: 8,
                                background: "#fff", overflow: "hidden",
                            }}>
                                {/* Header bar */}
                                <div style={{
                                    padding: "10px 14px", background: sc.bg,
                                    display: "flex", justifyContent: "space-between", alignItems: "center",
                                    borderBottom: `1px solid ${sc.border}`,
                                }}>
                                    <div>
                                        <span style={{ fontWeight: 700, fontSize: 15 }}>
                                            #{r.id} — {r.store_name}
                                        </span>
                                        <span style={{ color: "#888", fontSize: 13, marginLeft: 8 }}>
                                            ({r.site_id})
                                        </span>
                                    </div>
                                    <span style={{
                                        padding: "2px 10px", borderRadius: 12, fontSize: 12,
                                        fontWeight: 600, textTransform: "uppercase",
                                        background: sc.border, color: "#fff",
                                    }}>
                                        {r.status}
                                    </span>
                                </div>

                                {/* Body */}
                                <div style={{ padding: 14 }}>
                                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 20px", fontSize: 14, marginBottom: 10 }}>
                                        {r.barcode && (
                                            <div><strong>Barcode:</strong> {r.barcode}</div>
                                        )}
                                        {r.mc_asset_name && (
                                            <div><strong>MC Asset:</strong> {r.mc_asset_name}</div>
                                        )}
                                        {r.classification && (
                                            <div><strong>Type:</strong> {r.classification}</div>
                                        )}
                                        {r.asset_description && (
                                            <div><strong>Description:</strong> {r.asset_description}</div>
                                        )}
                                        <div><strong>Submitted by:</strong> {r.submitted_by_name || r.submitted_by}</div>
                                        <div><strong>Date:</strong> {new Date(r.submitted_at).toLocaleString()}</div>
                                        {r.mc_synced ? (
                                            <div style={{ color: "#16a34a" }}><strong>MC Synced:</strong> Yes</div>
                                        ) : null}
                                    </div>

                                    {/* Photos */}
                                    {photos.length > 0 && (
                                        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", marginBottom: 10 }}>
                                            {photos.map((p, i) => (
                                                <div
                                                    key={i}
                                                    onClick={() => setPreviewImage(p)}
                                                    style={{
                                                        width: 80, height: 60, borderRadius: 6,
                                                        border: "1px solid #e2e8f0", cursor: "pointer",
                                                        background: "#f8fafc", display: "flex",
                                                        alignItems: "center", justifyContent: "center",
                                                        overflow: "hidden",
                                                    }}
                                                >
                                                    <Image size={20} color="#cbd5e1" />
                                                </div>
                                            ))}
                                        </div>
                                    )}

                                    {/* Review info (if already reviewed) */}
                                    {r.reviewed_by && (
                                        <div style={{
                                            fontSize: 13, color: "#888", padding: "8px 0",
                                            borderTop: "1px solid #f1f5f9",
                                        }}>
                                            Reviewed by {r.reviewed_by} on {new Date(r.reviewed_at).toLocaleString()}
                                            {r.review_notes && <span> — "{r.review_notes}"</span>}
                                        </div>
                                    )}

                                    {/* Action buttons (only for pending) */}
                                    {r.status === "pending" && (
                                        <div style={{ display: "flex", gap: 8, marginTop: 8 }}>
                                            <button
                                                onClick={() => handleApprove(r.id)}
                                                disabled={actioningId === r.id}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 6,
                                                    padding: "8px 16px", background: "#22c55e", color: "#fff",
                                                    border: "none", borderRadius: 6, cursor: "pointer",
                                                    fontSize: 14, fontWeight: 500,
                                                    opacity: actioningId === r.id ? 0.5 : 1,
                                                }}
                                            >
                                                <CheckCircle2 size={16} />
                                                {actioningId === r.id ? "Approving..." : "Approve"}
                                            </button>
                                            <button
                                                onClick={() => { setShowRejectModal(r.id); setRejectNotes(""); }}
                                                disabled={actioningId === r.id}
                                                style={{
                                                    display: "flex", alignItems: "center", gap: 6,
                                                    padding: "8px 16px", background: "#fff", color: "#ef4444",
                                                    border: "1px solid #fecaca", borderRadius: 6, cursor: "pointer",
                                                    fontSize: 14, fontWeight: 500,
                                                }}
                                            >
                                                <XCircle size={16} />
                                                Reject
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Reject modal */}
            {showRejectModal && (
                <div style={{
                    position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                    zIndex: 1000, padding: 16,
                }}>
                    <div style={{
                        background: "#fff", borderRadius: 12, padding: 24,
                        width: "100%", maxWidth: 400,
                    }}>
                        <h3 style={{ margin: "0 0 12px" }}>Reject Request</h3>
                        <textarea
                            value={rejectNotes}
                            onChange={e => setRejectNotes(e.target.value)}
                            placeholder="Reason for rejection (optional)"
                            rows={3}
                            style={{
                                width: "100%", padding: "8px 10px", border: "1px solid #d1d5db",
                                borderRadius: 6, fontSize: 14, resize: "vertical", boxSizing: "border-box",
                            }}
                        />
                        <div style={{ display: "flex", gap: 8, marginTop: 12 }}>
                            <button
                                onClick={handleReject}
                                disabled={actioningId === showRejectModal}
                                style={{
                                    flex: 1, padding: "10px 16px", background: "#ef4444", color: "#fff",
                                    border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14,
                                    opacity: actioningId === showRejectModal ? 0.5 : 1,
                                }}
                            >
                                {actioningId === showRejectModal ? "Rejecting..." : "Reject"}
                            </button>
                            <button
                                onClick={() => setShowRejectModal(null)}
                                style={{
                                    padding: "10px 16px", background: "#f1f5f9", color: "#333",
                                    border: "none", borderRadius: 6, cursor: "pointer", fontSize: 14,
                                }}
                            >
                                Cancel
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Image preview */}
            {previewImage && (
                <div
                    onClick={() => setPreviewImage(null)}
                    style={{
                        position: "fixed", inset: 0, background: "rgba(0,0,0,0.85)",
                        display: "flex", alignItems: "center", justifyContent: "center",
                        zIndex: 9999, cursor: "pointer",
                    }}
                >
                    <img
                        src={previewImage}
                        alt="Submission photo"
                        style={{ maxWidth: "90vw", maxHeight: "85vh", objectFit: "contain", borderRadius: 8 }}
                    />
                </div>
            )}
        </div>
    );
}

export default AssetReview;
